import { useEffect, useRef } from 'react'
import { useAiInsights } from '@/features/ai-insights/hooks/useAiInsights'
import type { AiInsight, Lens } from '@/features/ai-insights/types'
import { InsightsList } from './InsightsList'

interface AiInsightsPanelProps {
  caseId: string | null
  lens?: Lens
  onOpenInGraph?: (insight: AiInsight) => void
  className?: string
}

/**
 * Top-level panel mounted inside the investigation sidebar's INSIGHTS tab.
 * Owns the data hook and tracks which insight ids arrived via probes during
 * this session (so they can render in the pinned "from your last action"
 * section).
 */
export function AiInsightsPanel({
  caseId,
  lens = 'graph',
  onOpenInGraph,
  className,
}: AiInsightsPanelProps) {
  const { insights, loading, refreshing, refresh, accept, skip } = useAiInsights(caseId, lens)

  // Track probe insights observed since mount.
  const sessionProbeIdsRef = useRef<Set<string>>(new Set())
  useEffect(() => {
    for (const i of insights) {
      if (i.generation_mode === 'probe') {
        sessionProbeIdsRef.current.add(i.id)
      }
    }
  }, [insights])

  return (
    <InsightsList
      insights={insights}
      loading={loading}
      refreshing={refreshing}
      sessionProbeIds={sessionProbeIdsRef.current}
      onAccept={accept}
      onSkip={skip}
      onOpenInGraph={onOpenInGraph}
      onGenerate={refresh}
      className={className}
    />
  )
}
